/**
 * Geometry Utilities
 *
 * Shared helper functions for geometry operations.
 * Eliminates code duplication across GeometriesList components.
 *
 * @module components/map/GeometriesList/utils
 */

import type { TFunction } from 'i18next';
import type { TaskGeometry } from '../../../../types';
import { GeometryType } from '../../../../types/geometry';

/**
 * Get the translated type label for a geometry
 *
 * Returns the localized name of the geometry type:
 * - EN: "Marker", "Line", "Shape"
 * - PL: "Znacznik", "Linia", "Kształt"
 *
 * @param type - The geometry type
 * @param t - Translation function from i18next
 * @returns Translated type label
 */
function getGeometryTypeLabel(type: GeometryType, t: TFunction): string {
  switch (type) {
    case GeometryType.Marker:
      return t('geometries.types.marker');
    case GeometryType.Line:
      return t('geometries.types.line');
    case GeometryType.Shape:
      return t('geometries.types.shape');
    default:
      return type;
  }
}

/**
 * Generate a default name for a new geometry
 *
 * Creates a name with format: "{Type} {Number}"
 * Examples:
 * - EN: "Line 1", "Marker 2", "Shape 3"
 * - PL: "Linia 1", "Znacznik 2", "Kształt 3"
 *
 * The number is auto-incremented based on existing geometries of the same type.
 * If there are already "Line 1" and "Line 2", the next will be "Line 3".
 *
 * @param type - The geometry type
 * @param existingGeometries - Array of existing geometries to check for numbering
 * @param t - Translation function from i18next
 * @returns Generated default name
 */
export function generateDefaultGeometryName(
  type: GeometryType,
  existingGeometries: TaskGeometry[],
  t: TFunction
): string {
  const typeLabel = getGeometryTypeLabel(type, t);

  // Find all geometries of the same type
  const sameTypeGeometries = existingGeometries.filter((g) => g.type === type);

  // Extract numbers from names like "Line 1", "Line 2", etc.
  const existingNumbers = sameTypeGeometries
    .map((g) => {
      // Try to extract number from name
      // Match patterns like "Line 1", "Linia 2", etc.
      const match = g.name.match(/\d+$/);
      return match ? parseInt(match[0], 10) : null;
    })
    .filter((num): num is number => num !== null);

  // Find the next available number
  const nextNumber =
    existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;

  return t('geometries.defaultName', { type: typeLabel, number: nextNumber });
}

/**
 * Filter out placeholder/empty geometries that shouldn't be displayed
 *
 * Excludes:
 * - Markers with isPlaceholder flag set to true
 * - Lines with no points (empty linePoints array)
 * - Shapes with no points (empty shapePoints array)
 *
 * @param geometries - Array of geometries to filter
 * @returns Filtered array of visible geometries
 */
export function filterVisibleGeometries(
  geometries: TaskGeometry[]
): TaskGeometry[] {
  return geometries.filter((geometry) => {
    if (geometry.type === GeometryType.Marker && geometry.isPlaceholder) {
      return false;
    }
    if (
      geometry.type === GeometryType.Line &&
      (geometry.linePoints?.length ?? 0) === 0
    ) {
      return false;
    }
    if (
      geometry.type === GeometryType.Shape &&
      (geometry.shapePoints?.length ?? 0) === 0
    ) {
      return false;
    }
    return true;
  });
}
